/** Simulation + render parameters — mirrors docs/math/MODEL.md */

import { keysToCamel } from "../core/snakeCamel";
import {
  LIVING_RECHOOSE_PROB,
  resolveSimDefaults,
  type DefaultsContext,
} from "./defaults";

export type { AudioParams } from "../audio/params";
export {
  audioParamsFromSnake,
  cloneAudioParams,
  defaultAudioParams,
  validateAudioParams,
} from "../audio/params";

export type Dim = 2 | 3;
export type RechooseMode = "independent" | "coupled";
export type InitPositions = string; // registry ids
export type Topology = string; // registry ids
export type ModelKind = string; // registry ids
/** Particle color encoding — DESIGNS-TRACK-X §2 + Track R type */
export type ColorMode = "velocity" | "cycle" | "uniform" | "type";
/**
 * Velocity colormap id (Canvas + WebGL).
 * Legacy alias `velocity_thermal` normalizes to `thermal` in colormap.ts.
 */
export type ColormapId =
  | "thermal"
  | "plasma"
  | "viridis"
  | "cividis"
  | "turbo"
  | "mono";
/** Point backend preference — DESIGNS-TRACK-X §6 */
export type RenderBackend = "auto" | "canvas" | "webgl";
/** Track M motion integrator */
export type MotionMode = "kinematic" | "inertial";
/** Track M gravity field */
export type GravityMode =
  | "none"
  | "origin"
  | "com"
  | "type_com"
  | "mutual"
  | "wells";
/** Explicit point-mass well (gravity_mode=wells) */
export type GravityWell = { x: number; y: number; z?: number; m?: number };

/** Nested type_rules blob — keys stay snake_case (shallow keysToCamel). */
export type TypeRulesBlob = {
  preset?: string;
  allow?: Record<string, number[][]>;
} | null;

export interface SimParams {
  n: number;
  dim: Dim;
  contraction: number;
  friendStep: number;
  enemyStep: number;
  softening: number;
  rechooseProb: number;
  rechooseMode: RechooseMode;
  rechooseCount: number;
  initPositions: InitPositions;
  topology: Topology;
  ringStride: number;
  forbidSelfLinks: boolean;
  freezeTopology: boolean;
  seed: number | null;
  model: ModelKind;
  /** Neighborhood radius for spatial hash / local rechoose / Reynolds */
  neighborRadius: number;
  /** Rebuild spatial hash and use it for Reynolds (and local rechoose) */
  spatialHash: boolean;
  /** When true, rechoose samples friend/enemy only inside neighborRadius */
  localTopology: boolean;
  /** Half-extent of jittered grid init sites (Track Y) */
  gridExtent: number;
  /** Absolute world-unit jitter on grid init */
  gridJitter: number;
  /** Track R: relation profile id (default classic_fe) */
  relationProfile: string;
  /** Track R: number of node types T ∈ [1,8] */
  typeCount: number;
  /** Track R: type_rules (nested snake_case); null = unconstrained when T=1 */
  typeRules: TypeRulesBlob;
  /** Track R: optional non-classic role weights; ignored on classic_fe */
  relationWeights: number[] | null;
  /** Track M: kinematic (default) or inertial velocity state */
  motionMode: MotionMode;
  /** Track M inertial damping δ */
  velocityDamping: number;
  /** Track M social gain α into velocity */
  socialGain: number;
  /** Track M max ‖v‖; 0 = uncapped */
  maxSpeed: number;
  /** Track M gravity field */
  gravityMode: GravityMode;
  gravityStrength: number;
  gravitySoftening: number;
  /** Mutual gravity radius; 0 → neighborRadius */
  gravityRadius: number;
  /**
   * Uniform inertia per point/voxel (F=ma): COM/mutual source weight and g = F/m.
   * Higher m → calmer under gravity; lower m → clumps. Not Newtonian GMm.
   * Does not change Woods social field u.
   */
  particleMass: number;
  /** Explicit wells when gravityMode=wells */
  wells: GravityWell[];
}

export interface RenderParams {
  pointSize: number;
  plotRange: number;
  colorScale: number;
  /**
   * Velocity speed → color ramp (only when colorMode is velocity).
   * All ramps keep a readable floor on black; default thermal.
   */
  colormap: ColormapId | string;
  /** @deprecated prefer colorMode === "velocity"; kept for URL/preset compat */
  velocityColor: boolean;
  /** velocity | cycle | uniform | type */
  colorMode: ColorMode;
  showTrails: boolean;
  trailFade: number;
  showFriendEdges: boolean;
  showEnemyEdges: boolean;
  /** Stroke width for friend/enemy edge lenses (CSS px) */
  edgeWidth: number;
  /** Opacity 0–1 applied to friend/enemy edge strokes */
  edgeOpacity: number;
  showDistanceMatrix: boolean;
  showMetrics: boolean;
  /** Pairwise matrix size (first m particles) */
  distanceMatrixSize: number;
  background: string;
  /** Point glyph: rect is faster; circle is smoother */
  pointStyle: "rect" | "circle";
  /** Clicked particle index, or -1 */
  selectedIndex: number;
  /** auto switches to webgl above threshold */
  backend: RenderBackend;
  /** Orbit camera (used when sim.dim === 3) — DESIGNS-TRACK-3D */
  cameraYaw: number;
  cameraPitch: number;
  cameraDistance: number;
  /** Perspective FOV degrees; 0 = orthographic */
  cameraFov: number;
  /** Auto yaw spin (rad/s); 0 = off */
  autoRotate: number;
  /** World-space coordinate grid lens (Track Y3) */
  showGrid: boolean;
  /** Half-extent of drawn grid (render-only; ≠ sim gridExtent) */
  gridDrawExtent: number;
  gridSpacing: number;
  gridOpacity: number;
  /** RGB axes triad at origin */
  showGnomon: boolean;
  /** Track M: draw velocity segments */
  showVelocityVectors: boolean;
}

/**
 * Global living-exhibit defaults (heuristic-resolved empty partial).
 * rechooseProb = 0.25 — not a frozen graph. See defaults.ts.
 */
export const defaultSimParams = (): SimParams =>
  validateSimParams(resolveSimDefaults({}));

export { LIVING_RECHOOSE_PROB, resolveSimDefaults };
export type { DefaultsContext };

export const defaultRenderParams = (): RenderParams => ({
  pointSize: 2.0,
  plotRange: 2.0,
  // Typical ‖v‖≈0.02 → t≈0.3 with scale 15 (mid ramp, not the dark floor).
  colorScale: 15,
  colormap: "thermal",
  velocityColor: true,
  colorMode: "velocity",
  showTrails: false,
  trailFade: 0.08,
  showFriendEdges: false,
  showEnemyEdges: false,
  edgeWidth: 1.5,
  edgeOpacity: 0.45,
  showDistanceMatrix: false,
  showMetrics: true,
  distanceMatrixSize: 12,
  background: "#000000",
  pointStyle: "rect",
  selectedIndex: -1,
  backend: "auto",
  cameraYaw: 0.55,
  cameraPitch: 0.35,
  cameraDistance: 3.2,
  cameraFov: 0,
  autoRotate: 0,
  showGrid: false,
  gridDrawExtent: 1.0,
  gridSpacing: 0.25,
  gridOpacity: 0.35,
  showGnomon: false,
  showVelocityVectors: false,
});

/** Effective color mode with legacy velocityColor fallback. */
export function effectiveColorMode(r: RenderParams): ColorMode {
  if (
    r.colorMode === "cycle" ||
    r.colorMode === "uniform" ||
    r.colorMode === "type"
  ) {
    return r.colorMode;
  }
  if (r.colorMode === "velocity") return "velocity";
  return r.velocityColor ? "velocity" : "uniform";
}

const VALID_RELATION_PROFILES = new Set(["classic_fe"]);
const VALID_MOTION = new Set(["kinematic", "inertial"]);
const VALID_GRAVITY = new Set([
  "none",
  "origin",
  "com",
  "type_com",
  "mutual",
  "wells",
]);

export function validateSimParams(p: SimParams): SimParams {
  if (p.n < 2) throw new Error(`n must be >= 2, got ${p.n}`);
  if (p.dim !== 2 && p.dim !== 3) throw new Error(`dim must be 2 or 3`);
  if (p.softening <= 0) throw new Error("softening must be > 0");
  if (p.rechooseProb < 0 || p.rechooseProb > 1) {
    throw new Error("rechooseProb must be in [0,1]");
  }
  if (p.typeCount < 1 || p.typeCount > 8) {
    throw new Error(`typeCount must be in [1,8], got ${p.typeCount}`);
  }
  if (!VALID_RELATION_PROFILES.has(p.relationProfile ?? "classic_fe")) {
    throw new Error(`unknown relationProfile: ${p.relationProfile}`);
  }
  if (p.relationWeights != null && !Array.isArray(p.relationWeights)) {
    throw new Error("relationWeights must be an array or null");
  }
  // Structural check only; full expand in typeRules module.
  // tribes_fe / two_tribes_fe work for any T (in-group F / out-group E).
  if (
    p.typeCount > 1 ||
    (p.typeRules && (p.typeRules as { preset?: string }).preset)
  ) {
    const tr = p.typeRules;
    if (tr && typeof tr === "object" && tr.preset && tr.preset !== "none") {
      const ok =
        tr.preset === "tribes_fe" ||
        tr.preset === "two_tribes_fe" ||
        tr.preset === "none";
      if (!ok) {
        throw new Error(`unknown type_rules preset: ${tr.preset}`);
      }
    }
  }
  const motion = p.motionMode ?? "kinematic";
  if (!VALID_MOTION.has(motion)) {
    throw new Error(`unknown motionMode: ${motion}`);
  }
  const grav = p.gravityMode ?? "none";
  if (!VALID_GRAVITY.has(grav)) {
    throw new Error(`unknown gravityMode: ${grav}`);
  }
  if (p.gravitySoftening != null && p.gravitySoftening <= 0) {
    throw new Error("gravitySoftening must be > 0");
  }
  if (p.maxSpeed != null && p.maxSpeed < 0) {
    throw new Error("maxSpeed must be >= 0");
  }
  if (p.wells != null && !Array.isArray(p.wells)) {
    throw new Error("wells must be an array");
  }
  return {
    ...p,
    motionMode: motion as MotionMode,
    gravityMode: grav as GravityMode,
    velocityDamping: p.velocityDamping ?? 0.2,
    socialGain: p.socialGain ?? 1.0,
    maxSpeed: p.maxSpeed ?? 0.08,
    gravityStrength: p.gravityStrength ?? 0.001,
    gravitySoftening: p.gravitySoftening ?? 0.05,
    gravityRadius: p.gravityRadius ?? 0,
    particleMass: (() => {
      const m = p.particleMass;
      if (m == null || !(m > 0) || !Number.isFinite(m)) return 1.0;
      // Match gravity floor — never allow near-zero 1/m explosions via URL/presets.
      return m < 0.25 ? 0.25 : m;
    })(),
    wells: Array.isArray(p.wells) ? p.wells : [],
  };
}

export function cloneSimParams(p: SimParams): SimParams {
  return {
    ...p,
    relationWeights: p.relationWeights ? [...p.relationWeights] : null,
    wells: p.wells ? p.wells.map((w) => ({ ...w })) : [],
    typeRules: p.typeRules
      ? {
          ...p.typeRules,
          allow: p.typeRules.allow
            ? Object.fromEntries(
                Object.entries(p.typeRules.allow).map(([k, m]) => [
                  k,
                  m.map((row) => [...row]),
                ]),
              )
            : undefined,
        }
      : null,
  };
}

export function cloneRenderParams(r: RenderParams): RenderParams {
  return { ...r };
}

/**
 * Merge snake_case partial (from shared JSON / URL) through heuristic defaults.
 * Only keys present in `partial` are treated as explicit — heuristics may fill
 * the rest (e.g. living rechoose, voxel freeze, mutual → spatial hash).
 */
export function simParamsFromSnake(
  partial: Record<string, unknown> | undefined | null,
  opts?: { tags?: readonly string[] },
): SimParams {
  const raw = partial ?? {};
  const camel = keysToCamel(raw) as Partial<SimParams>;
  const explicit = new Set(
    Object.keys(camel).filter(
      (k) => (camel as Record<string, unknown>)[k] !== undefined,
    ),
  );
  return validateSimParams(
    resolveSimDefaults(camel, { explicit, tags: opts?.tags }),
  );
}

/** Merge snake_case partial onto defaults → RenderParams */
export function renderParamsFromSnake(
  partial: Record<string, unknown> | undefined | null,
): RenderParams {
  const camel = keysToCamel(partial ?? {}) as Partial<RenderParams>;
  const merged = { ...defaultRenderParams(), ...camel };
  // Legacy presets: velocity_color false → uniform if color_mode omitted
  if (
    partial &&
    !("color_mode" in partial) &&
    !("colorMode" in (camel as object)) &&
    camel.velocityColor === false
  ) {
    merged.colorMode = "uniform";
  }
  return merged;
}
